import React, { useMemo } from 'react';
import { Heading } from '../catalyst/heading';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '../catalyst/table';
import { Badge } from '../catalyst/badge';
import { useChatUsers } from '../hooks/useConversation';

function formatTimestamp(iso: string): string {
	const date = new Date(iso);
	const now = new Date();
	const isToday =
		date.getFullYear() === now.getFullYear() &&
		date.getMonth() === now.getMonth() &&
		date.getDate() === now.getDate();
	if (isToday) {
		return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	}
	return (
		date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
		' ' +
		date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
	);
}

function truncate(text: string, maxLen: number): string {
	if (text.length <= maxLen) return text;
	return text.slice(0, maxLen) + '…';
}

interface ChatUser {
	userId: string;
	displayName: string | null;
	phoneNumber: string;
	lastContent: string | null;
	lastDirection: string;
	lastTime: string;
	messageCount: number;
}

const ChatsPage: React.FC = () => {
	const { data: messages, loading, error } = useChatUsers();

	// Group by user_id, take latest message per user, count messages
	const chatUsers = useMemo<ChatUser[]>(() => {
		if (!messages || messages.length === 0) return [];

		const userMap = new Map<string, ChatUser>();

		for (const msg of messages) {
			// Messages are already ordered desc by created_at
			const existing = userMap.get(msg.user_id);
			if (existing) {
				existing.messageCount++;
				continue;
			}
			userMap.set(msg.user_id, {
				userId: msg.user_id,
				displayName: msg.users?.display_name ?? null,
				phoneNumber: msg.users?.phone_number ?? '',
				lastContent: msg.content,
				lastDirection: msg.direction,
				lastTime: msg.created_at,
				messageCount: 1,
			});
		}

		// Sort: users waiting for reply (inbound) first, then by time descending
		return Array.from(userMap.values()).sort((a, b) => {
			const aWaiting = a.lastDirection === 'INBOUND' ? 0 : 1;
			const bWaiting = b.lastDirection === 'INBOUND' ? 0 : 1;
			if (aWaiting !== bWaiting) return aWaiting - bWaiting;
			return new Date(b.lastTime).getTime() - new Date(a.lastTime).getTime();
		});
	}, [messages]);

	return (
		<div className="p-6">
			<Heading level={1}>Chats</Heading>

			{loading && (
				<p className="mt-4 text-sm text-zinc-500">Loading conversations…</p>
			)}

			{error && (
				<p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>
			)}

			{!loading && !error && chatUsers.length === 0 && (
				<p className="mt-4 text-sm text-zinc-500">No conversations found.</p>
			)}

			{!loading && chatUsers.length > 0 && (
				<Table className="mt-6">
					<TableHead>
						<TableRow>
							<TableHeader>User</TableHeader>
							<TableHeader>Last Message</TableHeader>
							<TableHeader>Messages</TableHeader>
							<TableHeader>Time</TableHeader>
							<TableHeader>Status</TableHeader>
						</TableRow>
					</TableHead>
					<TableBody>
						{chatUsers.map((user) => (
							<TableRow key={user.userId} href={`/chats/${user.userId}`}>
								<TableCell>
									<div className="flex items-center gap-2">
										{user.lastDirection === 'INBOUND' && (
											<span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full bg-orange-400" />
										)}
										<div>
											<p className="font-medium text-zinc-900 dark:text-white">
												{user.displayName || user.phoneNumber || 'Unknown'}
											</p>
											{user.displayName && (
												<p className="text-xs text-zinc-500">{user.phoneNumber}</p>
											)}
										</div>
									</div>
								</TableCell>
								<TableCell className="text-zinc-600 dark:text-zinc-400">
									{user.lastContent
										? truncate(user.lastContent, 60)
										: <span className="italic text-zinc-400">[media]</span>}
								</TableCell>
								<TableCell className="text-zinc-500">
									{user.messageCount}
								</TableCell>
								<TableCell className="whitespace-nowrap text-zinc-500">
									{formatTimestamp(user.lastTime)}
								</TableCell>
								<TableCell>
									{user.lastDirection === 'INBOUND' ? (
										<Badge color="orange">Waiting</Badge>
									) : (
										<Badge color="zinc">Replied</Badge>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</div>
	);
};

export default ChatsPage;
